import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link, useNavigate, useParams } from 'react-router-dom';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import EmailRoundedIcon from '@mui/icons-material/EmailRounded';
import ImageNotSupportedRoundedIcon from '@mui/icons-material/ImageNotSupportedRounded';
import LocalShippingRoundedIcon from '@mui/icons-material/LocalShippingRounded';
import PersonRoundedIcon from '@mui/icons-material/PersonRounded';
import PhoneRoundedIcon from '@mui/icons-material/PhoneRounded';
import PlaceRoundedIcon from '@mui/icons-material/PlaceRounded';
import ReceiptLongRoundedIcon from '@mui/icons-material/ReceiptLongRounded';
import ShoppingBagRoundedIcon from '@mui/icons-material/ShoppingBagRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded';
import ContentCutRoundedIcon from '@mui/icons-material/ContentCutRounded';
import TextureRoundedIcon from '@mui/icons-material/TextureRounded';
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Divider,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from '@mui/material';
import DashboardPageHeader from 'pages/dashboard/DashboardPageHeader';
import VendorDashboardLayout from 'pages/dashboard/VendorDashboardLayout';
import OrderHistory from './OrderHistory';
import OrderStatusSelect from './OrderStatusSelect';
import PaymentStatusControl from './PaymentStatusControl';
import { downloadOrderReceipt, downloadProductionNotes } from './orderReceiptDownload';
import {
  formatOrderDate,
  formatOrderMoney,
  formatPaymentType,
  getAddressLines,
  getCustomerName,
  getOrderNumber
} from './orderUtils';

import { getProductionNotes } from './productionNotes';

function DetailLine({ label, children, strong = false }) {
  return (
    <Stack direction="row" justifyContent="space-between" alignItems="flex-start" gap={2}>
      <Typography color="text.secondary" variant="body2">
        {label}
      </Typography>
      <Typography
        variant={strong ? 'subtitle1' : 'body2'}
        fontWeight={strong ? 800 : 650}
        textAlign="right"
      >
        {children || '—'}
      </Typography>
    </Stack>
  );
}

function ContactLine({ icon: Icon, children }) {
  return (
    <Stack direction="row" alignItems="center" gap={1.25}>
      <Icon sx={{ color: 'text.secondary', fontSize: 19 }} />
      <Typography sx={{ overflowWrap: 'anywhere' }}>{children || '—'}</Typography>
    </Stack>
  );
}

function ItemAttributes({ attributes }) {
  if (!attributes?.length) return null;

  return (
    <Stack direction="row" flexWrap="wrap" mt={0.75} sx={{ gap: 0.75 }}>
      {attributes.map((attribute) => {
        const needsCentimetres = ['Ширина', 'Дължина', 'Височина'].includes(attribute.label);
        return (
          <Chip
            key={`${attribute.label}-${attribute.value}`}
            size="small"
            variant="outlined"
            label={`${attribute.label}: ${attribute.value}${needsCentimetres ? ' см' : ''}`}
          />
        );
      })}
    </Stack>
  );
}

function ItemPriceSummary({ item }) {
  return (
    <Typography
      variant="body2"
      aria-label={`Цена на ${item.productName}: ${formatOrderMoney(item.unitPrice, item.currency)} по ${item.quantity} броя, общо ${formatOrderMoney(item.finalPrice, item.currency)}`}
      sx={{ color: 'text.secondary', mt: 1 }}
    >
      Единична цена: {formatOrderMoney(item.unitPrice, item.currency)} × Количество: {item.quantity}{' '}
      ={' '}
      <Box component="span" sx={{ color: 'text.primary', fontWeight: 800 }}>
        {formatOrderMoney(item.finalPrice, item.currency)}
      </Box>
    </Typography>
  );
}

function ProductImage({ item }) {
  return item.imageUrl ? (
    <Box
      component="img"
      src={item.imageUrl}
      alt={item.productName}
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        flexShrink: 0,
        height: 96,
        objectFit: 'cover',
        width: 96
      }}
    />
  ) : (
    <Box
      aria-label="Няма снимка"
      sx={{
        alignItems: 'center',
        bgcolor: 'grey.100',
        borderRadius: 2,
        color: 'grey.400',
        display: 'flex',
        flexShrink: 0,
        height: 96,
        justifyContent: 'center',
        width: 96
      }}
    >
      <ImageNotSupportedRoundedIcon />
    </Box>
  );
}

export default function OrderDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [items, setItems] = useState([]);
  const [materialAllocations, setMaterialAllocations] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [statusError, setStatusError] = useState('');
  const [isStatusSaving, setIsStatusSaving] = useState(false);
  const [paymentError, setPaymentError] = useState('');
  const [isPaymentSaving, setIsPaymentSaving] = useState(false);
  const [deleteError, setDeleteError] = useState('');
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isDownloadingReceipt, setIsDownloadingReceipt] = useState(false);
  const [receiptError, setReceiptError] = useState('');
  const [isDownloadingProductionNotes, setIsDownloadingProductionNotes] = useState(false);
  const [productionNotesError, setProductionNotesError] = useState('');
  const [historyRefreshKey, setHistoryRefreshKey] = useState(0);

  useEffect(() => {
    let isCurrent = true;

    const loadOrder = async () => {
      setIsLoading(true);
      setError('');
      try {
        const { data } = await axios.get(`/orders/${id}`);
        if (!isCurrent) return;
        setOrder(data.order);
        setItems(data.items || []);
        setMaterialAllocations(data.materialAllocations || []);
      } catch (requestError) {
        if (!isCurrent) return;
        setError(
          requestError.response?.status === 404
            ? 'Тази поръчка не беше намерена.'
            : 'Детайлите за поръчката не можаха да бъдат заредени.'
        );
      } finally {
        if (isCurrent) setIsLoading(false);
      }
    };

    loadOrder();
    return () => {
      isCurrent = false;
    };
  }, [id]);

  if (isLoading) {
    return (
      <VendorDashboardLayout>
        <Card
          sx={{ alignItems: 'center', display: 'flex', justifyContent: 'center', minHeight: 320 }}
        >
          <CircularProgress aria-label="Зареждане на поръчката" />
        </Card>
      </VendorDashboardLayout>
    );
  }

  if (error || !order) {
    return (
      <VendorDashboardLayout>
        <Alert
          severity="error"
          action={
            <Button component={Link} to="/orders" color="inherit" size="small">
              Към поръчките
            </Button>
          }
        >
          {error || 'Тази поръчка не беше намерена.'}
        </Alert>
      </VendorDashboardLayout>
    );
  }

  const addressLines = getAddressLines(order);
  const productionNotes = getProductionNotes(items);

  const changeStatus = async (status) => {
    if (status === order.status) return;
    setIsStatusSaving(true);
    setStatusError('');
    try {
      const { data } = await axios.patch(`/orders/${id}/status`, {
        status,
        version: order.version || 0
      });
      setOrder(data.order);
      setHistoryRefreshKey((current) => current + 1);
    } catch (requestError) {
      setStatusError(
        requestError.response?.status === 409
          ? 'Поръчката е променена от друг администратор. Презаредете страницата.'
          : 'Статусът не можа да бъде запазен.'
      );
    } finally {
      setIsStatusSaving(false);
    }
  };

  const changePaymentStatus = async (paymentStatus) => {
    if (paymentStatus === order.paymentStatus) return;
    setIsPaymentSaving(true);
    setPaymentError('');
    try {
      const { data } = await axios.patch(`/orders/${id}/payment-status`, {
        paymentStatus,
        version: order.version || 0
      });
      setOrder(data.order);
      setHistoryRefreshKey((current) => current + 1);
    } catch (requestError) {
      setPaymentError(
        requestError.response?.status === 409
          ? 'Поръчката е променена от друг администратор. Презаредете страницата.'
          : 'Статусът на плащането не можа да бъде запазен.'
      );
    } finally {
      setIsPaymentSaving(false);
    }
  };

  const deleteOrder = async () => {
    setIsDeleting(true);
    setDeleteError('');
    try {
      await axios.delete(`/orders/${id}`, {
        data: { version: order.version || 0 }
      });
      navigate('/orders');
    } catch (requestError) {
      setDeleteError(
        requestError.response?.status === 409
          ? 'Поръчката е променена от друг администратор. Презаредете страницата.'
          : 'Поръчката не можа да бъде изтрита.'
      );
      setIsDeleteDialogOpen(false);
    } finally {
      setIsDeleting(false);
    }
  };

  const downloadReceipt = async () => {
    setIsDownloadingReceipt(true);
    setReceiptError('');
    try {
      await downloadOrderReceipt(order);
    } catch (requestError) {
      setReceiptError('Дигиталната бележка не можа да бъде изтеглена. Моля, опитайте отново.');
    } finally {
      setIsDownloadingReceipt(false);
    }
  };

  const downloadFactoryNotes = async () => {
    setIsDownloadingProductionNotes(true);
    setProductionNotesError('');
    try {
      await downloadProductionNotes(order);
    } catch (requestError) {
      setProductionNotesError(
        'Бележките за производство не можаха да бъдат изтеглени. Моля, опитайте отново.'
      );
    } finally {
      setIsDownloadingProductionNotes(false);
    }
  };

  return (
    <VendorDashboardLayout>
      <DashboardPageHeader
        title={`Поръчка #${getOrderNumber(order)}`}
        subtitle={`Направена на ${formatOrderDate(order.createdAt)}`}
        icon={ShoppingBagRoundedIcon}
        actionsBelow
        keepTitleOnOneLine
        actions={
          <Stack direction={{ xs: 'column', sm: 'row' }} flexWrap="wrap" gap={1}>
            <Button
              component={Link}
              to={`/orderDetails/${id}/edit`}
              variant="contained"
              startIcon={<EditRoundedIcon />}
            >
              Редактирай
            </Button>
            <Button
              variant="outlined"
              startIcon={<DownloadRoundedIcon />}
              onClick={downloadReceipt}
              disabled={isDownloadingReceipt}
            >
              {isDownloadingReceipt ? 'Подготвяне…' : 'Дигитална бележка'}
            </Button>
            <Button
              variant="outlined"
              startIcon={<ContentCutRoundedIcon />}
              onClick={downloadFactoryNotes}
              disabled={isDownloadingProductionNotes}
            >
              {isDownloadingProductionNotes ? 'Подготвяне…' : 'Свали бележки за производство'}
            </Button>
            <Button
              color="error"
              variant="outlined"
              startIcon={<DeleteOutlineRoundedIcon />}
              onClick={() => setIsDeleteDialogOpen(true)}
              disabled={isDeleting}
            >
              Изтрий
            </Button>
            <Button
              component={Link}
              to="/orders"
              variant="outlined"
              startIcon={<ArrowBackRoundedIcon />}
            >
              Всички поръчки
            </Button>
          </Stack>
        }
      />

      {statusError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setStatusError('')}>
          {statusError}
        </Alert>
      )}
      {paymentError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setPaymentError('')}>
          {paymentError}
        </Alert>
      )}
      {deleteError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setDeleteError('')}>
          {deleteError}
        </Alert>
      )}
      {receiptError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setReceiptError('')}>
          {receiptError}
        </Alert>
      )}
      {productionNotesError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setProductionNotesError('')}>
          {productionNotesError}
        </Alert>
      )}

      <Card sx={{ mb: 3, p: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={6}>
            <Typography variant="overline" color="text.secondary">
              Статус на поръчката
            </Typography>
            <OrderStatusSelect
              value={order.status}
              onChange={changeStatus}
              disabled={isStatusSaving}
              label={`Статус на поръчка ${getOrderNumber(order)}`}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Stack direction="row" flexWrap="wrap" gap={1} justifyContent={{ md: 'flex-end' }}>
              {order.editedAt && (
                <Chip color="secondary" label={`Редактирана: ${formatOrderDate(order.editedAt)}`} />
              )}
              {order.isBankPayment && (
                <>
                  <Chip color="info" label="По БАНКА" />
                  <PaymentStatusControl
                    paymentStatus={order.paymentStatus}
                    onChange={changePaymentStatus}
                    saving={isPaymentSaving}
                  />
                </>
              )}
            </Stack>
          </Grid>
        </Grid>
      </Card>

      <Grid container spacing={3} mb={3}>
        <Grid item xs={12} md={6} lg={4}>
          <Card sx={{ height: '100%', p: 3 }}>
            <Stack direction="row" alignItems="center" gap={1} mb={2.5}>
              <PersonRoundedIcon color="primary" />
              <Typography variant="h6">Клиент</Typography>
            </Stack>
            <Stack spacing={1.5}>
              <ContactLine icon={PersonRoundedIcon}>{getCustomerName(order)}</ContactLine>
              <ContactLine icon={EmailRoundedIcon}>{order.email}</ContactLine>
              <ContactLine icon={PhoneRoundedIcon}>{order.phone}</ContactLine>
            </Stack>
          </Card>
        </Grid>

        <Grid item xs={12} md={6} lg={4}>
          <Card sx={{ height: '100%', p: 3 }}>
            <Stack direction="row" alignItems="center" gap={1} mb={2.5}>
              <PlaceRoundedIcon color="primary" />
              <Typography variant="h6">Адрес за доставка</Typography>
            </Stack>
            {addressLines.length ? (
              addressLines.map((line, index) => (
                <Typography key={`${line}-${index}`}>{line}</Typography>
              ))
            ) : (
              <Typography>—</Typography>
            )}
            {order.shippingType && (
              <Stack direction="row" alignItems="center" gap={1} mt={2}>
                <LocalShippingRoundedIcon sx={{ color: 'text.secondary', fontSize: 19 }} />
                <Typography variant="body2" color="text.secondary">
                  {order.shippingType}
                </Typography>
              </Stack>
            )}
          </Card>
        </Grid>

        <Grid item xs={12} lg={4}>
          <Card sx={{ height: '100%', p: 3 }}>
            <Stack direction="row" alignItems="center" gap={1} mb={2.5}>
              <ReceiptLongRoundedIcon color="primary" />
              <Typography variant="h6">Обобщение</Typography>
            </Stack>
            <Stack spacing={1.5}>
              <DetailLine label="Начин на доставка">{order.shippingType}</DetailLine>
              <DetailLine label="Предпочитано плащане">
                {formatPaymentType(order.paymentType)}
              </DetailLine>
              {order.isBankPayment && (
                <DetailLine label="Статус на плащането">
                  {order.paymentStatus === 'paid'
                    ? `Платена${order.paidAt ? ` · ${formatOrderDate(order.paidAt)}` : ''}`
                    : 'Неплатена'}
                </DetailLine>
              )}
              <DetailLine label="Междинна сума" strong>
                {formatOrderMoney(order.subtotal, order.currency)}
              </DetailLine>
              <DetailLine label="Такса доставка">
                {formatOrderMoney(order.shippingPrice, order.currency)}
              </DetailLine>
              <Divider />
              <DetailLine label="Общо">{formatOrderMoney(order.total, order.currency)}</DetailLine>
            </Stack>
          </Card>
        </Grid>
      </Grid>

      <Card>
        <Box sx={{ p: 3 }}>
          <Stack direction="row" alignItems="center" gap={1}>
            <ShoppingBagRoundedIcon color="primary" />
            <Typography variant="h6">Продукти ({items.length})</Typography>
          </Stack>
        </Box>
        <Divider />

        {items.length === 0 ? (
          <Typography color="text.secondary" sx={{ p: 3 }}>
            Няма запазени продукти за тази поръчка.
          </Typography>
        ) : (
          <>
            <TableContainer sx={{ display: { xs: 'none', md: 'block' } }}>
              <Table aria-label="Продукти в поръчката">
                <TableHead>
                  <TableRow>
                    <TableCell>Продукт</TableCell>
                    <TableCell align="center">Количество</TableCell>
                    <TableCell align="right">Единична цена</TableCell>
                    <TableCell align="right">Крайна цена</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {items.map((item) => (
                    <TableRow key={item.id}>
                      <TableCell>
                        <Stack direction="row" alignItems="center" gap={2}>
                          <ProductImage item={item} />
                          <Box>
                            <Typography fontWeight={700}>{item.productName}</Typography>
                            {item.productCode && (
                              <Typography variant="caption" color="text.secondary">
                                Код: {item.productCode}
                              </Typography>
                            )}
                            <ItemAttributes attributes={item.attributes} />
                            <ItemPriceSummary item={item} />
                          </Box>
                        </Stack>
                      </TableCell>
                      <TableCell align="center">
                        <Chip label={item.quantity} size="small" color="primary" />
                      </TableCell>
                      <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                        {formatOrderMoney(item.unitPrice, item.currency)}
                      </TableCell>
                      <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                        <Typography fontWeight={800}>
                          {formatOrderMoney(item.finalPrice, item.currency)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <Stack divider={<Divider />} sx={{ display: { xs: 'flex', md: 'none' } }}>
              {items.map((item) => (
                <Box key={item.id} sx={{ p: 2.5 }}>
                  <Stack direction="row" alignItems="center" gap={2}>
                    <ProductImage item={item} />
                    <Box>
                      <Typography fontWeight={750}>{item.productName}</Typography>
                      {item.productCode && (
                        <Typography variant="caption" color="text.secondary">
                          Код: {item.productCode}
                        </Typography>
                      )}
                      <ItemAttributes attributes={item.attributes} />
                      <ItemPriceSummary item={item} />
                    </Box>
                  </Stack>
                </Box>
              ))}
            </Stack>
          </>
        )}
      </Card>

      {materialAllocations.length > 0 && (
        <Card sx={{ mt: 3, p: 3 }}>
          <Stack direction="row" alignItems="center" gap={1} mb={2}>
            <TextureRoundedIcon color="primary" />
            <Typography variant="h6">Отписани материали</Typography>
          </Stack>
          <Stack direction="row" flexWrap="wrap" gap={1}>
            {materialAllocations.map((allocation) => (
              <Chip
                key={allocation.materialInventoryId}
                variant="outlined"
                label={`${allocation.material?.code || 'Материал'} · ${allocation.meters} м`}
              />
            ))}
          </Stack>
        </Card>
      )}

      <Card sx={{ mt: 3, p: 3 }}>
        <Stack direction="row" alignItems="center" gap={1} mb={2}>
          <ReceiptLongRoundedIcon color="primary" />
          <Typography variant="h6">Бележки за производство</Typography>
        </Stack>

        {productionNotes.notes.length > 0 && (
          <Stack spacing={1}>
            {productionNotes.notes.map((note) => (
              <Typography key={note.text} fontWeight={700} sx={{ whiteSpace: 'pre-wrap' }}>
                {note.text}
              </Typography>
            ))}
          </Stack>
        )}

        {productionNotes.notes.length === 0 && productionNotes.issues.length === 0 && (
          <Typography color="text.secondary">
            В тази поръчка няма продукти с формула за производство.
          </Typography>
        )}

        {productionNotes.issues.map((item) => (
          <Alert key={item.id} severity="warning" sx={{ mt: 1.5 }}>
            {item.message}
          </Alert>
        ))}
      </Card>

      {order.note && (
        <Card sx={{ mt: 3, p: 3 }}>
          <Typography variant="h6" mb={1}>
            Бележка от клиента
          </Typography>
          <Typography color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>
            {order.note}
          </Typography>
        </Card>
      )}
      <OrderHistory orderId={id} refreshKey={historyRefreshKey} />
      <Dialog
        open={isDeleteDialogOpen}
        onClose={() => !isDeleting && setIsDeleteDialogOpen(false)}
        aria-labelledby="delete-order-title"
      >
        <DialogTitle id="delete-order-title">Изтриване на поръчка</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Поръчка #{getOrderNumber(order)} ще бъде скрита от списъците и отчетите. Данните и
            историята ѝ ще останат запазени и няма да бъдат изтрити от базата данни. Отписаните
            материали ще бъдат върнати в наличност.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsDeleteDialogOpen(false)} disabled={isDeleting}>
            Отказ
          </Button>
          <Button color="error" variant="contained" onClick={deleteOrder} disabled={isDeleting}>
            {isDeleting ? 'Изтриване…' : 'Изтрий поръчката'}
          </Button>
        </DialogActions>
      </Dialog>
    </VendorDashboardLayout>
  );
}
